'use client'
import React, { useState, useRef } from 'react'

import { CMSImage, Props as ImageProps } from '@/ui/atoms/CMSImage'
import { MediaBlock as MediaBlockType } from '@/payload-types'
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/ui/components/Dialog'
import { Icon } from '@/ui/atoms/Icon'
import { Button } from '@/ui/atoms/Button'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'

export const ImagesGridMedia: React.FC<ImageProps & MediaBlockType> = (props) => {
  const { gallery } = props
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)

  if (!gallery || gallery.length === 0) return null

  const handlePrevious = () => {
    if (selectedImageIndex === null) return
    sendGAEvent('event', 'imageGalleryPrevClicked')
    trackEvent('imageGalleryPrevClicked')
    setSelectedImageIndex((prev) => {
      if (prev === null) return 0
      return prev === 0 ? gallery.length - 1 : prev - 1
    })
  }

  const handleNext = () => {
    if (selectedImageIndex === null) return
    sendGAEvent('event', 'imageGalleryNextClicked')
    trackEvent('imageGalleryNextClicked')
    setSelectedImageIndex((prev) => {
      if (prev === null) return 0
      return prev === gallery.length - 1 ? 0 : prev + 1
    })
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]!.clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0]!.clientX
  }

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return

    const swipeDistance = touchEndX.current - touchStartX.current
    const minSwipeDistance = 50

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        handlePrevious()
      } else {
        handleNext()
      }
    }

    touchStartX.current = null
    touchEndX.current = null
  }

  return (
    <>
      <div className={'relative w-full columns-2 xl:columns-3 gap-grid-gutter'}>
        {gallery.map((galleryItem, index) => {
          if (!galleryItem?.image?.resource) return null

          return (
            <Dialog key={index}>
              <DialogTrigger asChild>
                <div
                  className={
                    'py-[10px] relative group cursor-pointer hover:opacity-50 transition-all break-inside-avoid '
                  }
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <CMSImage
                    className="rounded-md w-full h-auto row-span-2 border-[1px] border-border-subtle"
                    {...galleryItem.image}
                    resource={galleryItem.image.resource}
                  />
                  {(galleryItem.image.caption || galleryItem.image.copyright) && (
                    <div
                      className={
                        'flex justify-between items-center absolute bottom-1 inset-x-1 w-auto h-auto ' +
                        'group-hover:opacity-100 opacity-0 transition-opacity duration-300 ' +
                        'bg-bg-subtle-blue rounded-b-md py-1 px-2 text-body-XS-regular text-text'
                      }
                    >
                      {galleryItem.image.caption && <span>{galleryItem.image.caption}</span>}
                      {galleryItem.image.copyright && <span>{galleryItem.image.copyright}</span>}
                    </div>
                  )}
                </div>
              </DialogTrigger>
              <DialogContent className="!w-screen !h-screen md:!w-[90vw] md:!h-[90vh] max-w-[1200px] aspect-4/3 !p-0 bg-[#505050] bg-opacity-80 backdrop-blur">
                <DialogTitle className="sr-only">
                  Image Gallery -{' '}
                  {selectedImageIndex !== null && gallery[selectedImageIndex]?.image?.caption
                    ? gallery[selectedImageIndex].image.caption
                    : `Image ${selectedImageIndex !== null ? selectedImageIndex + 1 : 1} of ${gallery.length}`}
                </DialogTitle>
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className="absolute left-4 top-0 bottom-0 flex items-center justify-center z-10 pointer-events-none">
                    <Button
                      onClick={handlePrevious}
                      className={`pointer-events-auto ${
                        selectedImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      aria-label="Previous image"
                      disabled={selectedImageIndex === 0}
                      variant="primaryInverted"
                      size="md"
                    >
                      <Icon name="arrow-left-line"></Icon>
                    </Button>
                  </div>
                  <div className="absolute right-4 top-0 bottom-0 flex items-center justify-center z-10 pointer-events-none">
                    <Button
                      onClick={handleNext}
                      className={`pointer-events-auto ${
                        selectedImageIndex === gallery.length - 1
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      aria-label="Next image"
                      disabled={selectedImageIndex === gallery.length - 1}
                      variant="primaryInverted"
                      size="md"
                    >
                      <Icon name={`arrow-right-line`} className={``}></Icon>
                    </Button>
                  </div>

                  {selectedImageIndex !== null && gallery[selectedImageIndex]?.image?.resource && (
                    <div
                      className="relative w-full h-full flex items-center justify-center touch-pan-y"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      <div className="max-w-full max-h-[85vh] flex items-center justify-center">
                        <CMSImage
                          className="max-h-[85vh] w-auto object-contain"
                          {...gallery[selectedImageIndex].image}
                          resource={gallery[selectedImageIndex].image.resource}
                          caption={undefined}
                          copyright={undefined}
                        />
                      </div>
                      {(gallery[selectedImageIndex].image.caption ||
                        gallery[selectedImageIndex].image.copyright) && (
                        <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-75 text-text p-4">
                          {gallery[selectedImageIndex].image.caption && (
                            <p className="text-body-S-regular">
                              {gallery[selectedImageIndex].image.caption}
                            </p>
                          )}
                          {gallery[selectedImageIndex].image.copyright && (
                            <p className="text-body-XS-regular text-text-faded">
                              © {gallery[selectedImageIndex].image.copyright}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )
        })}
      </div>
    </>
  )
}
